import { useMemo } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import { WidgetsMileageModel } from "@interfaces/models";
import { useCommonStore, useUserStore } from "@stores/index";
import { formatAmount, formatMileage } from "@utils/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  ArrowUpRightSvg,
} from "@components/index";

export const WidgetMileageDetails = ({
  mileage,
  businessId,
  startDate,
  endDate,
  onClose,
}: {
  mileage: WidgetsMileageModel;
  businessId: string;
  startDate: string;
  endDate: string;
  onClose: () => void;
}) => {
  const setCommonState = useCommonStore(({ setState }) => setState);
  const businesses = useUserStore(({ businesses }) => businesses);

  const businessName = useMemo(
    () =>
      businesses?.find(({ uniqueId }) => uniqueId === businessId)?.name ?? "",
    [businessId, businesses],
  );

  const getLink = (assetId?: string) =>
    `/mileage?${new URLSearchParams({
      businessId,
      assetId: assetId ?? "",
      startDate: startDate ?? "",
      endDate: endDate ?? "",
    }).toString()}`;

  const handleClose = () => {
    setCommonState({ isDrawerShown: false });
    onClose();
  };

  const { summary, items } = mileage;

  return (
    <Drawer>
      <DrawerHeader
        onClose={handleClose}
        title={
          <div className="flex flex-col items-center md:items-start">
            <div className="text-300 font-bold text-black-100 md:text-600">
              Mileage
            </div>
            <div className="text-black-54 md:text-300 md:font-bold">
              {businessName}
            </div>
          </div>
        }
      />
      <DrawerScrollableContainer>
        <div className="flex flex-col pt-6 md:flex-row md:pt-14">
          <Link
            to={getLink("none")}
            className="z-[1] grid flex-[6] gap-6 rounded-lg bg-secondary-100 pb-4 pl-4 pr-4 pt-3.5 transition-opacity hover:opacity-80 md:pl-5"
          >
            <div className="flex items-center justify-between">
              <p className="font-bold text-white-100">Mileage:</p>
              <span className="text-white-54 *:size-6">
                <ArrowUpRightSvg />
              </span>
            </div>
            <p className="text-400 font-bold text-white-100">
              {summary.totalMiles
                ? formatMileage(summary.totalMiles)
                : formatAmount(summary.totalMileageCost, "£")}
            </p>
          </Link>
          <div className="-mt-4 flex grow-[5] gap-0.5 md:-ml-3 md:mt-0">
            <div
              className={clsx(
                "grid flex-1 gap-6 bg-primary-108 p-4 pt-8 md:pl-9 md:pt-4",
                summary.privateMiles
                  ? "rounded-bl-lg md:rounded-bl-none"
                  : "rounded-bl-lg rounded-br-lg md:rounded-bl-none md:rounded-tr-lg",
              )}
            >
              <p className="font-bold text-black-54">Business:</p>
              <p className="text-400 font-bold">
                {summary.businessMiles
                  ? formatMileage(summary.businessMiles)
                  : formatMileage(summary.totalBusinessMiles ?? 0)}
              </p>
            </div>
            {summary.privateMiles && (
              <div className="grid flex-1 gap-6 rounded-br-lg bg-primary-108 p-4 pt-8 md:rounded-tr-lg md:pl-6 md:pt-4">
                <p className="font-bold text-black-54">Private:</p>
                <p className="text-400 font-bold">
                  {formatMileage(summary.privateMiles ?? 0)}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 grid gap-8 md:mt-10">
          {items.map((m, i) => (
            <div key={`${m.assetUniqueId}${i}`} className="text-300">
              <Link
                to={getLink(m.assetUniqueId)}
                className="group flex items-center justify-between gap-2 hover:text-secondary-100"
              >
                <p className="text-400 font-bold">{m.assetName}</p>
                <span className="text-black-54 *:size-5 group-hover:text-secondary-100 md:*:size-6">
                  <ArrowUpRightSvg />
                </span>
              </Link>
              <div className="flex items-center justify-between gap-2 border-b border-b-black-16 py-6">
                <p className="text-black-70">Business</p>
                <p>
                  {m.businessMiles
                    ? formatMileage(m.businessMiles)
                    : formatMileage(m.mileageAmount ?? 0)}
                </p>
              </div>
              <div className="flex items-center justify-between gap-2 border-b border-b-black-16 py-6">
                <p className="text-black-70">
                  {m.privateMiles ? "Private" : "Mileage"}
                </p>
                <p>
                  {m.privateMiles
                    ? formatMileage(m.privateMiles)
                    : formatAmount(m.mileageCost, "£")}
                </p>
              </div>
            </div>
          ))}
        </div>
      </DrawerScrollableContainer>
    </Drawer>
  );
};
